import type { Category } from '../types';

/** Outbound channel derived from contact category labels. */
export type ContactChannel =
  | 'email'
  | 'phone'
  | 'whatsapp'
  | 'instagram'
  | 'tiktok'
  | 'facebook'
  | 'twitter'
  | 'linkedin'
  | 'youtube'
  | 'website'
  | 'other';

const CHANNEL_OPEN_LABEL_KEYS: Partial<Record<ContactChannel, string>> = {
  email: 'messages.gmailOpenInGmail',
  phone: 'messages.openSms',
  whatsapp: 'messages.openWhatsApp',
  instagram: 'messages.openInstagram',
  tiktok: 'messages.openTikTok',
  facebook: 'messages.openFacebook',
  twitter: 'messages.openTwitter',
  linkedin: 'messages.openLinkedIn',
  youtube: 'messages.openYouTube',
  website: 'messages.openWebsite',
};

/** Channels where the open URL can carry the message body. */
export function contactOpenPrefillsMessage(channel: ContactChannel): boolean {
  return channel === 'email' || channel === 'whatsapp' || channel === 'phone';
}

export function contactOpenLabelKey(channel: ContactChannel): string | null {
  return CHANNEL_OPEN_LABEL_KEYS[channel] ?? null;
}

export function resolveContactChannel(category: Category | null | undefined): ContactChannel {
  if (!category) return 'other';
  const raw = [category.label_fr, category.label_en, category.label]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (raw.includes('whatsapp')) return 'whatsapp';
  if (raw.includes('email') || raw.includes('e-mail') || raw.includes('courriel') || raw.includes('mail')) {
    return 'email';
  }
  if (raw.includes('téléphone') || raw.includes('telephone') || raw.includes('phone') || raw.includes('mobile')) {
    return 'phone';
  }
  if (raw.includes('instagram')) return 'instagram';
  if (raw.includes('tiktok')) return 'tiktok';
  if (raw.includes('facebook')) return 'facebook';
  if (raw.includes('twitter') || raw.includes('x.com')) return 'twitter';
  if (raw.includes('linkedin')) return 'linkedin';
  if (raw.includes('youtube')) return 'youtube';
  if (raw.includes('website') || raw.includes('site') || raw.includes('web')) return 'website';
  return 'other';
}

/**
 * Quick open from lead ⋯ menu: socials, website, and Gmail compose.
 * WhatsApp/SMS stay out — "Envoyer un message" already covers those flows.
 */
const MENU_OPEN_CHANNELS = new Set<ContactChannel>([
  'email',
  'instagram',
  'tiktok',
  'facebook',
  'twitter',
  'linkedin',
  'youtube',
  'website',
]);

export function contactChannelOpensFromMenu(channel: ContactChannel): boolean {
  return MENU_OPEN_CHANNELS.has(channel);
}

/** Label + URL for lead menu "open contact" (social, website, or Gmail compose). */
export function getLeadContactOpenAction(
  category: Category | null | undefined,
  contactValue: string | undefined,
  t: (key: string) => string
): { label: string; url: string } | null {
  const value = contactValue?.trim();
  if (!value) return null;

  const channel = resolveContactChannel(category);
  if (!contactChannelOpensFromMenu(channel)) return null;

  const labelKey = contactOpenLabelKey(channel);
  if (!labelKey) return null;

  const url = buildContactOpenUrl(channel, value);
  if (!url) return null;

  return { label: t(labelKey), url };
}

function asHttpUrl(value: string): string | null {
  const v = value.trim();
  if (!v) return null;
  if (/^https?:\/\//i.test(v)) return v;
  if (/^[\w.-]+\.[a-z]{2,}/i.test(v) && v.includes('/')) {
    return `https://${v}`;
  }
  return null;
}

/** Strip @ and known profile path prefixes to a bare handle. */
function socialHandle(value: string, hosts: string[]): string {
  let v = value.trim().replace(/^@/, '');
  for (const host of hosts) {
    const re = new RegExp(`^(?:https?:\\/\\/)?(?:www\\.)?${host.replace('.', '\\.')}\\/?`, 'i');
    v = v.replace(re, '');
  }
  return v.split(/[/?#]/)[0]?.replace(/^@/, '') ?? '';
}

function digitsPhone(value: string): string {
  return value.replace(/[^\d+]/g, '');
}

function waMeNumber(value: string): string | null {
  const digits = value.replace(/\D/g, '');
  return digits.length >= 8 ? digits : null;
}

/**
 * Build an URL to open the contact channel.
 * Returns null when we cannot open anything useful.
 */
export function buildContactOpenUrl(
  channel: ContactChannel,
  rawValue: string,
  opts?: { subject?: string; body?: string }
): string | null {
  const value = rawValue?.trim();
  if (!value) return null;

  switch (channel) {
    case 'email': {
      // Gmail web compose (creators almost always use Gmail, not mailto OS client)
      const params = new URLSearchParams({ view: 'cm', fs: '1', to: value });
      if (opts?.subject) params.set('su', opts.subject);
      if (opts?.body) params.set('body', opts.body);
      return `https://mail.google.com/mail/?${params.toString()}`;
    }
    case 'whatsapp': {
      const n = waMeNumber(value);
      if (!n) return null;
      const text = opts?.body ? `?text=${encodeURIComponent(opts.body)}` : '';
      return `https://wa.me/${n}${text}`;
    }
    case 'phone': {
      const phone = digitsPhone(value);
      if (!phone) return null;
      if (opts?.body) {
        return `sms:${phone}?body=${encodeURIComponent(opts.body)}`;
      }
      return `tel:${phone}`;
    }
    case 'instagram': {
      const http = asHttpUrl(value);
      if (http) return http;
      const handle = socialHandle(value, ['instagram.com']);
      return handle ? `https://www.instagram.com/${handle}/` : null;
    }
    case 'tiktok': {
      const http = asHttpUrl(value);
      if (http) return http;
      // Accept @user, user, tiktok.com/@user, vm.tiktok.com/...
      if (/tiktok\.com/i.test(value)) {
        return value.startsWith('http') ? value : `https://${value.replace(/^\/\//, '')}`;
      }
      const handle = socialHandle(value, ['tiktok.com', 'vm.tiktok.com']).replace(/^@/, '');
      return handle ? `https://www.tiktok.com/@${handle}` : null;
    }
    case 'facebook': {
      const http = asHttpUrl(value);
      if (http) return http;
      const handle = socialHandle(value, ['facebook.com', 'fb.com']);
      return handle ? `https://www.facebook.com/${handle}` : null;
    }
    case 'twitter': {
      const http = asHttpUrl(value);
      if (http) return http;
      const handle = socialHandle(value, ['twitter.com', 'x.com']);
      return handle ? `https://x.com/${handle}` : null;
    }
    case 'linkedin': {
      const http = asHttpUrl(value);
      if (http) return http;
      if (/linkedin\.com/i.test(value)) {
        return value.startsWith('http') ? value : `https://${value.replace(/^\/\//, '')}`;
      }
      // Profile slug: jane-doe / in/jane-doe (no spaces)
      const slug = value
        .trim()
        .replace(/^@/, '')
        .replace(/^in\//i, '')
        .replace(/^company\//i, '');
      if (/^[a-zA-Z0-9][a-zA-Z0-9_-]{1,100}$/.test(slug)) {
        return `https://www.linkedin.com/in/${slug}`;
      }
      return null;
    }
    case 'youtube': {
      const http = asHttpUrl(value);
      if (http) return http;
      const handle = socialHandle(value, ['youtube.com', 'youtu.be']);
      return handle ? `https://www.youtube.com/@${handle.replace(/^@/, '')}` : null;
    }
    case 'website': {
      return asHttpUrl(value) ?? (value.includes('.') ? `https://${value}` : null);
    }
    default:
      return asHttpUrl(value);
  }
}
